import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { BookOpen, LogOut, Inbox, Calendar, GraduationCap, Info } from "lucide-react";
import BrandLogo from "./BrandLogo";
import { BRAND } from "../lib/brand";

const CALENDLY_URL = "https://calendly.com/ramzybaroody";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="sticky top-0 z-40">
      {/* PhD disclaimer strip — LMS-only, thin gold bar at the very top */}
      <div
        data-testid="phd-disclaimer-strip"
        className="bg-[var(--gold)] text-[var(--bg-deep)] text-center text-[11px] sm:text-xs font-semibold tracking-wide px-4 py-1.5 flex items-center justify-center gap-2"
      >
        <GraduationCap className="w-3.5 h-3.5" strokeWidth={2.5} />
        <span>All courses are taught by a Ph.D. College Professor</span>
      </div>
      <nav className="border-b-2 border-[var(--gold)] bg-[var(--bg-deep)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <BrandLogo size="md" showText={false} />
            <Link to="/dashboard" data-testid="nav-home-link" className="min-w-0 hover:opacity-90 transition-opacity">
              <div className="font-display text-base sm:text-lg font-bold leading-tight truncate" title={BRAND.name}>
                {BRAND.name}
              </div>
              <div className="text-[10px] sm:text-[11px] text-[var(--muted-text)] uppercase tracking-wider truncate">
                {BRAND.tagline}
              </div>
            </Link>
          </div>

          {user && user !== false && (
            <div className="flex items-center gap-3 shrink-0">
              <Link to="/dashboard" data-testid="nav-courses-link" className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg text-[var(--gold)] hover:bg-[var(--paper-soft)] transition-colors">
                <BookOpen className="w-4 h-4" /> Courses
              </Link>
              <Link to="/about" data-testid="nav-about-link" className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg text-[var(--gold)] hover:bg-[var(--paper-soft)] transition-colors">
                <Info className="w-4 h-4" /> About
              </Link>
              {user.role === "instructor" && (
                <Link to="/leads" data-testid="nav-leads-link" className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg text-[var(--gold)] hover:bg-[var(--paper-soft)] transition-colors">
                  <Inbox className="w-4 h-4" /> Leads
                </Link>
              )}
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-schedule-link"
                className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg text-[var(--gold)] hover:bg-[var(--paper-soft)] transition-colors"
                title="Schedule a meeting on Calendly (opens in new tab)"
              >
                <Calendar className="w-4 h-4" /> Schedule
              </a>
              <div className="hidden md:block px-3 py-1.5 rounded-full border-2 border-[var(--gold)] bg-[var(--paper)] text-[var(--gold)] text-xs font-semibold">
                <span data-testid="nav-user-name">{user.name}</span>
                <span className="ml-2 text-[var(--muted-text)]">·</span>
                <span className="ml-2 uppercase text-[10px] tracking-wider" data-testid="nav-user-role">{user.role}</span>
              </div>
              <button data-testid="nav-logout-btn" onClick={handleLogout} className="nb-btn nb-btn-white text-sm flex items-center gap-2">
                <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          )}
        </div>
      </nav>
    </div>
  );
}
